import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandableLineItem from './ExpandableLineItem.jsx';
import AccountMatchPanel from './AccountMatchPanel.jsx';
import { PRODUCT_STATUS_CONFIG, normalizeProductStatus } from './constants.js';
import { ColumnShell, Section } from './DrawerPrimitives.jsx';
import { money, signedMoney } from './drawerFormat.js';
import { VARIANCE_SOURCE_LABELS } from './varianceActions.js';
import { draftAddsForFile, draftPreviewForProduct } from './varianceActionPreview.js';
import {
  buildDateLookupByProductCode,
  draftAddContext,
  firstDisplayDate,
  formatEvidenceDate,
  groupedByMonth,
  inheritedDateForCode,
  productCardDate,
  productCardMeta,
  productFamilyKey,
  renderProductFamilyLanes,
  sortEvidenceProducts,
} from './DrawerEvidenceHelpers.jsx';
import { LiveEvidenceComparisonPanel } from './DrawerLiveSourcePanels.jsx';
import { productWarningState } from './productWarningState.js';
import { totalDeltaRowsForFile } from './varianceActionTotals.js';
import { draftActionBillability, sourceBillability } from './productBillability.js';

export { sortEvidenceProducts };

const accountCandidateCount = (appsheetData = null) => {
  const ids = new Set();
  (appsheetData?.accounts || []).forEach((account) => {
    const id = account.salesforceAccountId || account.salesforceId || account.accountId || account.id || account.name;
    if (id) ids.add(id);
  });
  (appsheetData?.orderItems || []).forEach((item) => {
    const id = item.salesforceAccountId || item.accountId || item.accountName || item.dealerName;
    if (id) ids.add(id);
  });
  return ids.size;
};

const matchesFocusedProduct = (code, focusProductCode) => (
  Boolean(code && focusProductCode && String(code).toUpperCase() === String(focusProductCode).toUpperCase())
);

const suggestionIndexForProducts = ({ suggestions = [], rows = [], codeForRow }) => {
  const duplicateFamilies = rows.reduce((acc, row) => {
    const family = productFamilyKey(codeForRow(row));
    if (family) acc.set(family, (acc.get(family) || 0) + 1);
    return acc;
  }, new Map());
  return suggestions.reduce((acc, suggestion) => {
    const family = productFamilyKey(suggestion.productCode || suggestion.productFamily);
    if (!family || duplicateFamilies.get(family) > 1) return acc;
    const code = String(suggestion.productCode || '').toUpperCase();
    if (code) acc.set(code, suggestion);
    return acc;
  }, new Map());
};

const suggestionForProduct = (suggestionsByCode, productCode = '') => {
  const family = productFamilyKey(productCode);
  if (!family) return null;
  return suggestionsByCode.get(String(productCode || '').toUpperCase()) || null;
};

const normalizedCode = (value) => String(value || '').trim().toUpperCase().replace(/_+$/, '');

const productCodeCounts = (rows = [], codeForRow = (row) => row.productCode) => rows.reduce((acc, row) => {
  const code = normalizedCode(codeForRow(row));
  if (code) acc.set(code, (acc.get(code) || 0) + 1);
  return acc;
}, new Map());

const isUniqueProductCode = (counts, code) => {
  const normalized = normalizedCode(code);
  return Boolean(normalized && counts.get(normalized) === 1);
};

const LiveEvidenceLoadingPanel = () => (
  <Stack spacing={0.75} aria-label="Loading live evidence">
    <Skeleton height={36} />
    <Skeleton height={82} />
    <Skeleton height={82} />
    <Skeleton height={82} />
  </Stack>
);

const inheritedCpqCodeForDisplay = (row = {}) => {
  const current = normalizedCode(row.oemProductCode || row.normalizedProductCode);
  const candidate = normalizedCode(row.inheritedProductCode || row.accountMatch?.productCode || row.accountMatch?.evidence?.productCode);
  if (!current || !candidate || current === candidate) return row.oemProductCode || row.normalizedProductCode || '';
  const currentFamily = productFamilyKey(current);
  return currentFamily && currentFamily !== 'UNCODED' && currentFamily === productFamilyKey(candidate) && current === currentFamily
    ? candidate
    : row.oemProductCode || row.normalizedProductCode || '';
};

const rawCpqCodeForInheritance = (row = {}) => (
  row.rawProductCode
  || row.originalCpqProductCode
  || row.productCode
  || row.normalizedProductCode
  || row.oemProductCode
  || ''
);

export const CpqColumn = ({ total, originalTotal = null, totalStatus = null, items, serviceMonth, program = 'website', trailingWindow = [], appsheetData = null, onVarianceMenu, onEvidenceDrop, onEvidenceRemoveDrop = null, draftActions = [], dense = false, productFamilyOrder = [], productFamilyMinRows = {}, focusProductCode = '', recurringSuggestions = [] }) => {
  const ordered = useMemo(() => {
    const rank = new Map(trailingWindow.map((month, index) => [month, index]));
    return [...(items || [])].sort((a, b) => {
      const rankA = rank.has(a.serviceMonth) ? rank.get(a.serviceMonth) : 99;
      const rankB = rank.has(b.serviceMonth) ? rank.get(b.serviceMonth) : 99;
      return rankA - rankB;
    });
  }, [items, trailingWindow]);
  const appsheetDatesByCode = useMemo(
    () => buildDateLookupByProductCode(appsheetData?.orderItems || []),
    [appsheetData],
  );

  const billing = useMemo(() => ordered.filter((row) => PRODUCT_STATUS_CONFIG[normalizeProductStatus(row.productStatus, '')]?.billing), [ordered]);
  const notBilling = useMemo(() => ordered.filter((row) => !PRODUCT_STATUS_CONFIG[normalizeProductStatus(row.productStatus, '')]?.billing), [ordered]);
  const draftAdds = useMemo(() => draftAddsForFile({ actions: draftActions, fileKind: 'cpq' }), [draftActions]);
  const billingWithDraftAdds = useMemo(
    () => [...billing, ...draftAdds.map((draft) => ({ ...draft, __draftAdd: true }))],
    [billing, draftAdds],
  );
  const accountCount = useMemo(() => accountCandidateCount(appsheetData), [appsheetData]);
  const totalDeltaRows = useMemo(() => totalDeltaRowsForFile(draftActions, 'cpq', {
    cpqRows: ordered,
  }), [draftActions, ordered]);
  const suggestionsByCode = useMemo(
    () => suggestionIndexForProducts({
      suggestions: recurringSuggestions,
      rows: ordered,
      codeForRow: (row) => row.oemProductCode,
    }),
    [ordered, recurringSuggestions],
  );
  const cpqProductCodeCounts = useMemo(
    () => productCodeCounts(ordered, (row) => inheritedCpqCodeForDisplay(row)),
    [ordered],
  );

  const renderRow = (row, key) => {
    const isCredit = Number(row.budget) < 0;
    const displayProductCode = inheritedCpqCodeForDisplay(row);
    const rawCpqProductCode = rawCpqCodeForInheritance(row);
    const originalCpqProductCode = displayProductCode !== rawCpqProductCode ? rawCpqProductCode : row.originalCpqProductCode;
    const hasInheritedDisplayCode = Boolean(originalCpqProductCode && displayProductCode !== originalCpqProductCode);
    const rowForWarnings = hasInheritedDisplayCode ? {
      ...row,
      displayProductCode,
      inheritedProductCode: displayProductCode,
      originalCpqProductCode,
      rawProductCode: originalCpqProductCode,
      oemProductCode: displayProductCode,
    } : row;
    const draftPreview = draftPreviewForProduct({
      actions: draftActions,
      fileKind: 'cpq',
      productCode: displayProductCode,
      row: rowForWarnings,
      allowWeakProductCodeMatch: isUniqueProductCode(cpqProductCodeCounts, displayProductCode),
    });
    return (
      <ExpandableLineItem
        key={key}
        primary={row.productName || displayProductCode}
        secondary={`${displayProductCode} - ${row.serviceMonth} - Qty ${row.quantity ?? '--'}`}
        meta={productCardMeta({
          code: displayProductCode,
          date: firstDisplayDate(productCardDate(row), inheritedDateForCode(appsheetDatesByCode, displayProductCode)),
        })}
        amount={signedMoney(row.budget)}
        amountProps={{ color: isCredit ? 'error.main' : 'inherit' }}
        billability={sourceBillability({ source: 'cpq', row, status: row.productStatus, serviceMonth, program })}
        muted={!row.isCurrentMonth}
        status={row.productStatus}
        onVarianceMenu={onVarianceMenu}
        onEvidenceDrop={onEvidenceDrop}
        draftPreview={draftPreview}
        codeReplacementPreview={originalCpqProductCode ? {
          original: originalCpqProductCode,
          replacement: displayProductCode,
          label: 'Inherited evidence',
        } : null}
        defaultOpen={matchesFocusedProduct(row.oemProductCode, focusProductCode)}
        dense={dense}
        warningState={productWarningState({
          row: rowForWarnings,
          draftPreview,
          accountCandidateCount: accountCount,
          recurringSuggestion: suggestionForProduct(suggestionsByCode, displayProductCode),
        })}
        varianceContext={{
          source: 'cpq',
          sourceLabel: VARIANCE_SOURCE_LABELS.cpq,
          productName: row.productName || displayProductCode,
          productCode: displayProductCode,
          displayProductCode,
          originalCpqProductCode,
          rawProductCode: hasInheritedDisplayCode ? originalCpqProductCode : '',
          inheritedProductCode: hasInheritedDisplayCode ? displayProductCode : row.inheritedProductCode,
          hasInheritedDisplayCode,
          price: Number(row.budget) || 0,
          status: row.productStatus,
          orderItemId: row.orderItemId || row.__orderItemId,
          sourceOrderItemId: row.orderItemId || row.__orderItemId,
          matchedAppSheetOrderItemId: row.matchedAppSheetOrderItemId || row.accountMatch?.orderItemId,
          referenceId: row.referenceId,
          row: rowForWarnings,
        }}
        details={{
          'Dealer Code': row.dealerCode,
          'Dealer Name': row.dealerName,
          'Product Name': row.productName,
          'OEM Product Code': displayProductCode,
          'Original CPQ Product Code': originalCpqProductCode,
          'Product Status': row.productStatus,
          'Invoice Group': row.invoiceGroup || row.accountMatch?.evidence?.invoiceGroup,
          Budget: money(row.budget),
          'Budget Currency': row.budgetCurrency,
          Provider: row.provider,
          'Provider Currency': row.providerCurrency,
          Shift: row.shift,
          'Shift Currency': row.shiftCurrency,
          'Service Month': row.serviceMonth,
          Quantity: row.quantity,
          'Usage Type': row.usageType,
          'Effective Date': formatEvidenceDate(row.effectiveDate),
          'Enrollment Date': formatEvidenceDate(row.enrollmentDate),
          'Activation Date': formatEvidenceDate(row.activationDate),
          'Inherited AppSheet Date': !row.effectiveDate && !row.activationDate ? formatEvidenceDate(inheritedDateForCode(appsheetDatesByCode, row.oemProductCode)) : undefined,
          'SF Match Status': row.accountMatch?.matchStatus,
          'SF Account': row.accountMatch?.salesforceAccountName,
          'SF Account ID': row.accountMatch?.salesforceAccountId,
          'Source CPQ Order Item ID': row.orderItemId || row.__orderItemId,
          'Matched AppSheet Order Item ID': row.matchedAppSheetOrderItemId || row.accountMatch?.orderItemId,
          'DPE Reference ID': row.accountMatch?.referenceId || row.referenceId,
          'SF Match Method': row.accountMatch?.matchMethod,
          'SF Match Confidence': row.accountMatch ? `${row.accountMatch.confidence}%` : undefined,
          'Invoice #': row.invoiceNumber,
          'Line type': isCredit ? 'Credit / reversal' : 'Charge',
        }}
      />
    );
  };
  const renderDraftAdd = (draft, key = `cpq-draft-add-${draft.id}`) => (
    <ExpandableLineItem
      key={key}
      primary={draft.productName || draft.productCode}
      secondary={draft.productCode}
      meta={productCardMeta({ code: draft.productCode, date: '' })}
      amount={signedMoney(Number(draft.price) || 0)}
      billability={draftActionBillability({ fileKind: 'cpq', action: draft.action, serviceMonth, program })}
      status={draft.status}
      onVarianceMenu={onVarianceMenu}
      onEvidenceDrop={onEvidenceDrop}
      draftPreview={{ variant: 'added', label: draft.label || 'Draft add', action: draft.action }}
      defaultOpen={matchesFocusedProduct(draft.productCode, focusProductCode)}
      dense={dense}
      warningState={productWarningState({ row: draft.action || {}, draftPreview: { action: draft.action }, accountCandidateCount: accountCount })}
      varianceContext={draftAddContext({ fileKind: 'cpq', sourceLabel: VARIANCE_SOURCE_LABELS.cpq, draft })}
      details={{
        'Draft Action': draft.action?.actionLabel,
        'Product Name': draft.productName,
        'OEM Product Code': draft.productCode,
        Budget: money(draft.price),
        Source: draft.action?.sourceLabel,
        Problem: draft.action?.problem,
        Fix: draft.action?.fix,
        Note: draft.action?.note,
      }}
    />
  );

  return (
    <ColumnShell
      label="DI CPQ File"
      total={total}
      originalTotal={originalTotal}
      totalStatus={totalStatus}
      totalDeltaRows={totalDeltaRows}
      accent="secondary.main"
      dropTarget={{ source: 'cpq', sourceLabel: VARIANCE_SOURCE_LABELS.cpq, isFileDrop: true }}
      removeTarget={{ source: 'cpq', sourceLabel: VARIANCE_SOURCE_LABELS.cpq }}
      onEvidenceDrop={onEvidenceDrop}
      onEvidenceRemoveDrop={onEvidenceRemoveDrop}
    >
      <Section title="Billing" count={billing.length + draftAdds.length} countColor={billing.length || draftAdds.length ? 'success' : 'warning'} emptyText="No billing rows in the trailing window">
        {renderProductFamilyLanes({
          rows: billingWithDraftAdds,
          codeForRow: (row) => (row.__draftAdd ? row.productCode : row.oemProductCode),
          renderRow: (row, key) => (row.__draftAdd ? renderDraftAdd(row, key) : renderRow(row, key)),
          keyPrefix: 'cpq-bill',
          familyOrder: productFamilyOrder,
          familyMinRows: productFamilyMinRows,
          showEmptyLanes: productFamilyOrder.length > 1,
          emptyLaneLabel: 'No CPQ product',
          dense,
        })}
      </Section>
      <Section title="Not billing" count={notBilling.length} defaultOpen={notBilling.length > 0} countColor={notBilling.length ? 'error' : 'default'} emptyText="No credits or cancellations in the trailing window">
        {groupedByMonth({ rows: notBilling, months: trailingWindow, serviceMonth, renderRow, keyPrefix: 'gm-nb' })}
      </Section>
    </ColumnShell>
  );
};

export const GmColumn = ({ total, originalTotal = null, totalStatus = null, items = [], serviceMonth = null, program = 'website', appsheetData = null, onVarianceMenu, onEvidenceDrop, onEvidenceRemoveDrop = null, draftActions = [], dense = false, productFamilyOrder = [], productFamilyMinRows = {}, focusProductCode = '', recurringSuggestions = [], label = 'GM Billing File', source = 'gm', sourceLabel = VARIANCE_SOURCE_LABELS.gm, accent = 'primary.main' }) => {
  const billing = useMemo(() => sortEvidenceProducts(items.filter((row) => row.isBilling)), [items]);
  const notBilling = useMemo(() => sortEvidenceProducts(items.filter((row) => !row.isBilling)), [items]);
  const draftAdds = useMemo(() => draftAddsForFile({ actions: draftActions, fileKind: 'gm' }), [draftActions]);
  const billingWithDraftAdds = useMemo(
    () => [...billing, ...draftAdds.map((draft) => ({ ...draft, __draftAdd: true }))],
    [billing, draftAdds],
  );
  const accountCount = useMemo(() => accountCandidateCount(appsheetData), [appsheetData]);
  const totalDeltaRows = useMemo(() => totalDeltaRowsForFile(draftActions, 'gm'), [draftActions]);
  const suggestionsByCode = useMemo(
    () => suggestionIndexForProducts({
      suggestions: recurringSuggestions,
      rows: items,
      codeForRow: (row) => row.productCode,
    }),
    [items, recurringSuggestions],
  );
  const gmProductCodeCounts = useMemo(() => productCodeCounts(items, (row) => row.productCode), [items]);
  const renderRow = (row, key, muted = false) => {
    const draftPreview = draftPreviewForProduct({
      actions: draftActions,
      fileKind: 'gm',
      productCode: row.productCode,
      row,
      allowWeakProductCodeMatch: isUniqueProductCode(gmProductCodeCounts, row.productCode),
    });
    return (
      <ExpandableLineItem
        key={key}
        primary={row.productSelection || row.productCode}
        secondary={row.productCode}
        meta={productCardMeta({
          code: row.productCode,
          date: productCardDate(row),
        })}
        amount={row.dealerCost}
        billability={sourceBillability({ source, row, status: row.productStatus, serviceMonth, program })}
        muted={muted}
        status={row.productStatus}
        onVarianceMenu={onVarianceMenu}
        onEvidenceDrop={onEvidenceDrop}
        draftPreview={draftPreview}
        defaultOpen={matchesFocusedProduct(row.productCode, focusProductCode)}
        dense={dense}
        warningState={productWarningState({
          row,
          draftPreview,
          accountCandidateCount: accountCount,
          recurringSuggestion: suggestionForProduct(suggestionsByCode, row.productCode),
        })}
        varianceContext={{
          source,
          sourceLabel,
          productName: row.productSelection || row.productCode,
          productCode: row.productCode,
          price: Number(row.dealerCost) || 0,
          status: row.productStatus,
          orderItemId: row.accountMatch?.orderItemId || row.orderItemId || row.__orderItemId,
          referenceId: row.accountMatch?.referenceId || row.referenceId,
          row,
        }}
        details={{
          BAC: row.bac,
          'Dealer Name': row.dealerName,
          'Vendor Name': row.vendorName,
          'Vendor Status': row.vendorStatus,
          'Is Terminated': row.isTerminated,
          'Is Terminated Date': formatEvidenceDate(row.isTerminatedDate),
          'Product Code': row.productCode,
          'Product Selection': row.productSelection,
          'Product Designation': row.productDesignation,
          'Product Brand': row.productBrand,
          'Product Status': row.productStatus,
          'Is Billing': row.isBilling,
          'Dealer Cost': money(row.dealerCost),
          'Enrolled Date': formatEvidenceDate(row.enrolledDate),
          'Enrollment Status': row.enrollmentStatus,
          'Opt Out Date': formatEvidenceDate(row.optOutDate),
          'Last Updated Date': formatEvidenceDate(row.lastUpdatedDate),
          'Product Selection Date': formatEvidenceDate(row.productSelectionDate),
          'Go Live Date': formatEvidenceDate(row.goLiveDate),
          'Cancel Date': formatEvidenceDate(row.cancelDate),
          'Billing Start Date': formatEvidenceDate(row.billingStartDate),
          'Billing End Date': formatEvidenceDate(row.billingEndDate),
          'Effective Date': formatEvidenceDate(row.effectiveDate),
          'SF Match Status': row.accountMatch?.matchStatus,
          'SF Account': row.accountMatch?.salesforceAccountName,
          'SF Account ID': row.accountMatch?.salesforceAccountId,
          'AppSheet Order Item ID': row.accountMatch?.orderItemId,
          'DPE Reference ID': row.accountMatch?.referenceId,
          'SF Match Method': row.accountMatch?.matchMethod,
          'SF Match Confidence': row.accountMatch ? `${row.accountMatch.confidence}%` : undefined,
          Notes: row.notes,
        }}
      />
    );
  };
  const renderDraftAdd = (draft, key = `gm-draft-add-${draft.id}`) => (
    <ExpandableLineItem
      key={key}
      primary={draft.productName || draft.productCode}
      secondary={draft.productCode}
      meta={productCardMeta({ code: draft.productCode, date: '' })}
      amount={Number(draft.price) || 0}
      billability={draftActionBillability({ fileKind: source, action: draft.action, serviceMonth, program })}
      status={draft.status}
      onVarianceMenu={onVarianceMenu}
      onEvidenceDrop={onEvidenceDrop}
      draftPreview={{ variant: 'added', label: draft.label || 'Draft add', action: draft.action }}
      defaultOpen={matchesFocusedProduct(draft.productCode, focusProductCode)}
      dense={dense}
      warningState={productWarningState({ row: draft.action || {}, draftPreview: { action: draft.action }, accountCandidateCount: accountCount })}
      varianceContext={draftAddContext({ fileKind: 'gm', sourceLabel: VARIANCE_SOURCE_LABELS.gm, draft })}
      details={{
        'Draft Action': draft.action?.actionLabel,
        'Product Name': draft.productName,
        'Product Code': draft.productCode,
        'Dealer Cost': money(draft.price),
        Source: draft.action?.sourceLabel,
        Problem: draft.action?.problem,
        Fix: draft.action?.fix,
        Note: draft.action?.note,
      }}
    />
  );

  return (
    <ColumnShell
      label={label}
      total={total}
      originalTotal={originalTotal}
      totalStatus={totalStatus}
      totalDeltaRows={totalDeltaRows}
      accent={accent}
      dropTarget={{ source, sourceLabel, isFileDrop: true }}
      removeTarget={source === 'gm' || source === 'cpq' ? { source, sourceLabel } : null}
      onEvidenceDrop={onEvidenceDrop}
      onEvidenceRemoveDrop={onEvidenceRemoveDrop}
    >
      <Section title="Billing" count={billing.length + draftAdds.length} countColor={billing.length || draftAdds.length ? 'success' : 'warning'} emptyText="No active GM billing lines">
        {renderProductFamilyLanes({
          rows: billingWithDraftAdds,
          codeForRow: (row) => row.productCode,
          renderRow: (row, key) => (row.__draftAdd ? renderDraftAdd(row, key) : renderRow(row, key)),
          keyPrefix: 'gm-bill',
          familyOrder: productFamilyOrder,
          familyMinRows: productFamilyMinRows,
          showEmptyLanes: productFamilyOrder.length > 1,
          emptyLaneLabel: 'No GM product',
          dense,
        })}
      </Section>
      <Section title="Not billing" count={notBilling.length} defaultOpen={false} emptyText="No non-billing GM rows">
        {renderProductFamilyLanes({ rows: notBilling, codeForRow: (row) => row.productCode, renderRow: (row, key) => renderRow(row, key, true), keyPrefix: 'gm-nb' })}
      </Section>
    </ColumnShell>
  );
};

export const LiveSourcesColumn = ({ dpeItems, dpeData, appsheetData, dpeActions = [], onVarianceMenu, onEvidenceDrop, onDpeActionDrop = null, onDpeActionRemoveDrop = null, onRefreshEvidence = null, isRefreshingEvidence = false, isLoadingEvidence = false, dense = false, productFamilyOrder = [], productFamilyMinRows = {}, focusProductCode = '', allowDpeActions = true, serviceMonth = null, program = 'website' }) => {
  const [accountMatchModalOpen, setAccountMatchModalOpen] = useState(false);
  const accountMatches = appsheetData?.accountMatches || [];
  const accountMatchesNeedingReview = accountMatches.filter((match) => match.matchStatus !== 'matched').length;
  const accent = 'success.main';
  const accountMatchColor = accountMatchesNeedingReview ? 'warning' : dpeData?.source?.cacheUpdatedAt ? 'success' : 'error';
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0, borderRadius: 1.5, borderTop: '3px solid', borderTopColor: accent }}>
      <Stack spacing={1.5}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            minHeight: 0,
            pb: 0,
          }}
          data-billing-column-header="true"
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography
              variant="overline"
              sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, lineHeight: 1.2, mb: 0.5 }}
            >
              Live Evidence
            </Typography>
            {onRefreshEvidence ? (
              <Tooltip title="Refresh this BAC's live evidence snapshot">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Refresh this BAC live evidence"
                    color="primary"
                    disabled={isRefreshingEvidence}
                    onClick={onRefreshEvidence}
                    sx={{ width: 28, height: 28 }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        </Box>
        <Stack spacing={1.25}>
          <Box data-billing-live-subpanel="appsheet">
            {isLoadingEvidence ? (
              <LiveEvidenceLoadingPanel />
            ) : (
              <LiveEvidenceComparisonPanel
                appsheetData={appsheetData}
                dpeItems={dpeItems}
                dpeActions={dpeActions}
                onVarianceMenu={onVarianceMenu}
                onEvidenceDrop={onEvidenceDrop}
                onDpeActionDrop={onDpeActionDrop}
                onDpeActionRemoveDrop={onDpeActionRemoveDrop}
                dense={dense}
                productFamilyOrder={productFamilyOrder}
                productFamilyMinRows={productFamilyMinRows}
                focusProductCode={focusProductCode}
                allowDpeActions={allowDpeActions}
                serviceMonth={serviceMonth}
                program={program}
                accountMatchAction={(
                  <Tooltip title="Open Salesforce account matching">
                    <IconButton
                      size="small"
                      color={accountMatchColor}
                      aria-label="Open Salesforce account matching"
                      onClick={() => setAccountMatchModalOpen(true)}
                      sx={{ width: 24, height: 24, flexShrink: 0 }}
                    >
                      <AccountTreeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              />
            )}
          </Box>
        </Stack>
      </Stack>
      <Dialog open={accountMatchModalOpen} onClose={() => setAccountMatchModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Salesforce account matching
          <IconButton
            aria-label="Close Salesforce account matching"
            onClick={() => setAccountMatchModalOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 10 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <AccountMatchPanel matches={accountMatches} defaultOpen />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
